import React, { useLayoutEffect, useRef, useState } from 'react';
import { Shirt, RotateCcw, User } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';
import useSyncedState from './useSyncedState';
import ReferenceScale from './ReferenceScale';

// Одягалка — a character picture plus a wardrobe of clothing items grouped
// into categories (hats, tops, shoes…). Only one item per category is worn at
// a time; categories later in the list are drawn on top. Each item sits over
// the character at { x, y, w } in % of the character box (defaults to a full
// overlay, which suits same-size transparent PNG "skins").
// Synced state: { outfit: { [categoryId]: itemId }, rev }.

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

export const DRESS_UP_DEFAULT_ASPECT = 3 / 4;

const createInitialState = () => ({ outfit: {} });

// Largest box with the given aspect ratio that fits inside the container.
export const useFitBox = (aspect) => {
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (!cw || !ch) return;
      const w = Math.min(cw, ch * aspect);
      setBox({ w, h: w / aspect });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [aspect]);
  return [ref, box];
};

// Character with the worn items layered on top. Shared with the config panel.
export const DressUpStage = ({ characterImage, categories, outfit, aspect, onAspect, children }) => {
  const [ref, box] = useFitBox(aspect);
  return (
    <div ref={ref} className="w-full h-full min-w-0 min-h-0 flex items-center justify-center">
      <div className="relative overflow-hidden" style={{ width: box.w, height: box.h }}>
        {characterImage ? (
          <img
            src={getMediaUrl(characterImage)}
            alt=""
            draggable={false}
            onLoad={(e) => {
              const { naturalWidth: nw, naturalHeight: nh } = e.currentTarget;
              if (onAspect && nw && nh) onAspect(nw / nh);
            }}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
            <User size={Math.max(24, Math.round(box.h * 0.4))} className="text-gray-300" />
          </div>
        )}
        {categories.map((cat) => {
          const item = (cat.items || []).find((it) => it.id === outfit[cat.id]);
          if (!item || !item.imageUrl) return null;
          return (
            <img
              key={cat.id}
              src={getMediaUrl(item.imageUrl)}
              alt={item.name || ''}
              draggable={false}
              className="absolute pointer-events-none select-none"
              style={{
                left: `${item.x ?? 0}%`,
                top: `${item.y ?? 0}%`,
                width: `${item.w ?? 100}%`,
                height: 'auto',
              }}
            />
          );
        })}
        {children}
      </div>
    </div>
  );
};

const DressUpInner = ({
  element,
  isEditor = false,
  canInteract = true,
  syncedState = null,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const {
    title = '',
    characterImage = null,
    characterAspect = DRESS_UP_DEFAULT_ASPECT,
    categories = [],
    accentColor = '#2A4D25',
    panelBgColor = '#f5f1ec',
    textColor = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 16,
    showNames = true,
  } = element.content || {};

  const [state, update] = useSyncedState(syncedState, onStateChange, createInitialState);
  const [aspect, setAspect] = useState(characterAspect || DRESS_UP_DEFAULT_ASPECT);
  const [activeCatId, setActiveCatId] = useState(null);
  const rootSizeRef = useRef(null);
  const [vertical, setVertical] = useState(false);

  useLayoutEffect(() => {
    const el = rootSizeRef.current;
    if (!el) return undefined;
    const measure = () => setVertical(el.clientWidth < el.clientHeight * 0.9);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const interactive = !isEditor && canInteract;
  // Only categories that actually have items show up in the wardrobe.
  const visibleCats = categories.filter((c) => (c.items || []).some((it) => it.imageUrl));
  const activeCat = visibleCats.find((c) => c.id === activeCatId) || visibleCats[0] || null;

  // Drop picks for categories/items the teacher has since removed.
  const outfit = Object.fromEntries(
    Object.entries(state.outfit || {}).filter(([catId, itemId]) => {
      const cat = categories.find((c) => c.id === catId);
      return cat && (cat.items || []).some((it) => it.id === itemId);
    })
  );
  const wornCount = Object.keys(outfit).length;

  const toggleItem = (catId, itemId) => {
    if (!interactive) return;
    update((prev) => {
      const next = { ...(prev.outfit || {}) };
      if (next[catId] === itemId) delete next[catId];
      else next[catId] = itemId;
      return { ...prev, outfit: next };
    });
  };

  return (
    <div
      ref={rootSizeRef}
      className="w-full h-full flex flex-col overflow-hidden"
      style={{ fontFamily, color: textColor, padding: 10, gap: 8 }}
    >
      {(title || (interactive && wornCount > 0)) && (
        <div className="shrink-0 flex items-center justify-center gap-2">
          {title && <div className="font-bold leading-tight text-center" style={{ fontSize: fontSize * 1.3 }}>{title}</div>}
          {interactive && wornCount > 0 && (
            <button
              type="button"
              onClick={() => update((prev) => ({ ...prev, outfit: {} }))}
              title={t('games.dressUp.reset', { defaultValue: 'Зняти все' })}
              className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            >
              <RotateCcw size={14} />
            </button>
          )}
        </div>
      )}

      <div className={`flex-1 min-h-0 flex ${vertical ? 'flex-col' : 'flex-row'}`} style={{ gap: 10 }}>
        <div className="min-w-0 min-h-0" style={{ flex: '1 1 50%' }}>
          <DressUpStage
            characterImage={characterImage}
            categories={categories}
            outfit={outfit}
            aspect={aspect}
            onAspect={(a) => { if (Number.isFinite(a) && Math.abs(a - aspect) > 0.001) setAspect(a); }}
          />
        </div>

        <div
          className="min-w-0 min-h-0 flex flex-col rounded-2xl overflow-hidden"
          style={{ flex: '1 1 50%', backgroundColor: panelBgColor, padding: 8, gap: 8 }}
        >
          {visibleCats.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400" style={{ gap: 6, fontSize: fontSize * 0.85 }}>
              <Shirt size={Math.round(fontSize * 2)} />
              <span>{t('games.dressUp.emptyWardrobe', { defaultValue: 'Додайте одяг у налаштуваннях' })}</span>
            </div>
          ) : (
            <>
              {visibleCats.length > 1 && (
                <div className="shrink-0 flex flex-wrap" style={{ gap: 6 }}>
                  {visibleCats.map((cat) => {
                    const active = activeCat && cat.id === activeCat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setActiveCatId(cat.id)}
                        className="rounded-full font-semibold transition leading-none"
                        style={{
                          fontSize: fontSize * 0.85,
                          fontFamily,
                          padding: '6px 12px',
                          backgroundColor: active ? accentColor : '#ffffff',
                          color: active ? '#ffffff' : textColor,
                          border: `2px solid ${active ? accentColor : 'rgba(0,0,0,0.06)'}`,
                        }}
                      >
                        {cat.name || '…'}
                      </button>
                    );
                  })}
                </div>
              )}
              <div
                className="flex-1 min-h-0 overflow-y-auto"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))',
                  gridAutoRows: 'min-content',
                  gap: 6,
                }}
              >
                {activeCat && (activeCat.items || []).filter((it) => it.imageUrl).map((item) => {
                  const worn = outfit[activeCat.id] === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleItem(activeCat.id, item.id)}
                      disabled={!interactive}
                      className="flex flex-col items-center rounded-xl transition"
                      style={{
                        backgroundColor: '#ffffff',
                        border: `2px solid ${worn ? accentColor : 'transparent'}`,
                        boxShadow: worn ? `0 0 0 2px ${accentColor}33` : 'none',
                        padding: 4,
                        gap: 2,
                        cursor: interactive ? 'pointer' : 'default',
                      }}
                    >
                      <div className="w-full flex items-center justify-center" style={{ aspectRatio: '1 / 1' }}>
                        <img
                          src={getMediaUrl(item.imageUrl)}
                          alt={item.name || ''}
                          draggable={false}
                          className="max-w-full max-h-full object-contain pointer-events-none select-none"
                        />
                      </div>
                      {showNames && item.name && (
                        <span className="w-full text-center leading-tight break-words" style={{ fontSize: fontSize * 0.75, fontFamily }}>
                          {item.name}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DressUpElement = ({ slideScale = 1, ...props }) => (
  <ReferenceScale scale={slideScale}>
    <DressUpInner {...props} />
  </ReferenceScale>
);

export default DressUpElement;
